import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Search, Loader2, ChevronRight, X, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// Bulk "Set Location": same geocoder + location builder as the single-item editor (one taxonomy, no new fields).
export const BulkLocationDialog = ({ open, onOpenChange, ids, onDone }) => {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [cands, setCands] = useState([]);
  const [picked, setPicked] = useState(null);
  const [status, setStatus] = useState(null); // {total, with_location, without_location}
  const [scope, setScope] = useState("all");  // all | missing
  const [saving, setSaving] = useState(false);
  const n = ids.length;

  useEffect(() => {
    if (!open) return;
    setQuery(""); setCands([]); setPicked(null); setStatus(null); setScope("all");
    api.post("/admin/media/bulk_location", { ids, dry_run: true }).then((r) => {
      setStatus(r.data);
      if (r.data.with_location > 0 && r.data.without_location > 0) setScope("missing");
    }).catch(() => setStatus({ total: n, with_location: 0, without_location: n }));
  }, [open]); // eslint-disable-line

  const search = async () => {
    const q = query.trim();
    if (!q) return;
    setSearching(true); setCands([]); setPicked(null);
    try {
      const r = await api.get("/geo/search", { params: { q } });
      const list = r.data?.candidates || [];
      if (!list.length) toast.error(`No place found for "${q}"`);
      else if (list.length === 1) setPicked(list[0]);
      else setCands(list);
    } catch { toast.error("Location lookup failed"); }
    setSearching(false);
  };

  const hasMixed = status && status.with_location > 0;
  const targetCount = scope === "missing" && status ? status.without_location : n;
  const save = async () => {
    if (!picked) return;
    setSaving(true);
    try {
      const r = await api.post("/admin/media/bulk_location", { ids, candidate: picked, only_missing: scope === "missing" });
      toast.success(`Location set to ${picked.label} on ${r.data.updated} item${r.data.updated === 1 ? "" : "s"}${r.data.skipped ? ` · ${r.data.skipped} kept their existing location` : ""}${r.data.ineligible_count ? ` · ${r.data.ineligible_count} skipped (not Originals + Candid)` : ""}`);
      onDone?.(r.data);
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set location"); }
    setSaving(false);
  };

  const path = picked ? (picked.path?.length ? picked.path.map((p) => p.name) : [picked.city, picked.region, picked.country].filter(Boolean)) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="bulk-location-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" aria-hidden="true" /> Set Location for {n} selected</DialogTitle>
          <DialogDescription>Type a place and the country, region and city are filled in from the same location system used on single items. Only the location field changes — tags, titles, descriptions, library and ratings stay exactly as they are.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input data-testid="bulk-loc-search" autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
            placeholder="Vancouver · British Columbia · Canada · YVR…" className="bg-secondary/50 border-border h-9 text-sm" />
          <Button data-testid="bulk-loc-search-btn" size="sm" onClick={search} disabled={searching || !query.trim()} className="rounded-full h-9 shrink-0">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Search className="w-4 h-4" aria-hidden="true" />}
          </Button>
        </div>
        {status?.ineligible?.length > 0 && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="bulk-loc-ineligible">
            {status.ineligible.length} of {n} selected can't take a location (rule: BareFootage Originals + manual Candid only) and will be skipped.
          </div>
        )}
        {cands.length > 0 && !picked && (
          <div className="space-y-1" data-testid="bulk-loc-candidates">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Which one?</div>
            {cands.map((c, i) => (
              <button key={`${c.osm_type}-${c.osm_id}-${i}`} type="button" data-testid={`bulk-loc-candidate-${i}`} onClick={() => { setPicked(c); setCands([]); }}
                className="w-full text-left rounded-md border border-border bg-background/50 hover:border-primary hover:bg-primary/5 px-2.5 py-1.5 transition-colors">
                <div className="text-sm">{c.label}</div>
                <div className="text-[11px] text-muted-foreground">{(c.path || []).map((p) => p.name).join(" › ")}</div>
              </button>
            ))}
          </div>
        )}
        {picked && (
          <div className="rounded-lg border border-primary/40 bg-primary/5 px-3 py-2.5" data-testid="bulk-loc-picked">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-primary mb-1">Location to apply</div>
                <div className="flex items-center flex-wrap gap-1 text-sm" data-testid="bulk-loc-path">
                  {path.map((nm, i) => (
                    <span key={i} className="inline-flex items-center gap-1">
                      <span className={i === path.length - 1 ? "text-primary font-medium" : "text-muted-foreground"}>{nm}</span>
                      {i < path.length - 1 && <ChevronRight className="w-3 h-3 opacity-50" aria-hidden="true" />}
                    </span>
                  ))}
                </div>
              </div>
              <button type="button" data-testid="bulk-loc-clear" onClick={() => setPicked(null)} aria-label="Choose a different place" className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" aria-hidden="true" /></button>
            </div>
          </div>
        )}
        {hasMixed && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs" data-testid="bulk-loc-mixed-warning">
            <div className="flex items-center gap-1.5 text-amber-300 font-medium mb-2"><AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" /> {status.with_location} of {status.total} selected item{status.total === 1 ? "" : "s"} already {status.with_location === 1 ? "has" : "have"} a location.</div>
            <div className="flex flex-col gap-1.5">
              <label className="inline-flex items-center gap-2 cursor-pointer"><input type="radio" name="bulk-loc-scope" data-testid="bulk-loc-scope-all" checked={scope === "all"} onChange={() => setScope("all")} /> Apply to all {status.total} selected (overwrites existing)</label>
              <label className="inline-flex items-center gap-2 cursor-pointer"><input type="radio" name="bulk-loc-scope" data-testid="bulk-loc-scope-missing" checked={scope === "missing"} onChange={() => setScope("missing")} /> Apply only to the {status.without_location} without a location</label>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between gap-3 pt-1">
          <div className="text-xs text-muted-foreground" data-testid="bulk-loc-summary">
            {picked ? <>Apply this location to <b className="text-foreground">{targetCount}</b> selected item{targetCount === 1 ? "" : "s"}</> : "Pick a place to continue"}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" data-testid="bulk-loc-cancel" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
            <Button size="sm" data-testid="bulk-loc-save" onClick={save} disabled={!picked || saving || targetCount === 0} className="rounded-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : `Save · ${targetCount}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
